import type { PageKey } from "common/analytics";

// Synchronously-maintained current page key, used as the `page_name` super property and as
// `entry_page` for CTAs. Falls back to "unknown" before the first route resolve / for unmapped
// routes, so callers always get a non-optional string.
let currentPageKey = "unknown";

// Hand-maintained route(pathname) -> generated PageKey map. Extend as more routes are
// instrumented. Order matters (first match wins). Values are typed as PageKey for safety.
const ROUTE_TO_PAGE: Array<{ match: (path: string) => boolean; page: PageKey }> = [
  { match: (p) => p === "/" || p === "" || p.startsWith("/index"), page: "home" },
  { match: (p) => p.startsWith("/search"), page: "search" },
  { match: (p) => p.startsWith("/venuedetails"), page: "place" },
  // Venue-detail family (basic/complex/guide/photos/reviews) — all resolve to a place-ish page so
  // their events don't report page_name "unknown". `-space` must precede `-guide` (prefix overlap).
  { match: (p) => p.startsWith("/complexvenuetabs"), page: "place" },
  { match: (p) => p.startsWith("/accessibility-guide-space"), page: "place_space" },
  { match: (p) => p.startsWith("/accessibility-guide"), page: "accessibility_guide" },
  { match: (p) => p.startsWith("/whats-nearby"), page: "area_map" },
  { match: (p) => p.startsWith("/venuephotos"), page: "place" },
  { match: (p) => p.startsWith("/venuereviews"), page: "place" },
  { match: (p) => p.startsWith("/your-badges"), page: "badges" },
  { match: (p) => p.startsWith("/mysavedplaces"), page: "saved_places" },
  { match: (p) => p.startsWith("/savedplacedetails"), page: "saved_place_list" },
  { match: (p) => p.startsWith("/notifications"), page: "notifications" },
  { match: (p) => p.startsWith("/settings"), page: "manage_notifications" },
  { match: (p) => p.startsWith("/your-accessibility-profile"), page: "accessibility_profile_form" },
  { match: (p) => p.startsWith("/your-profile"), page: "manage_account" },
  { match: (p) => p.startsWith("/your-activity"), page: "activity" },
  { match: (p) => p.startsWith("/your-teams"), page: "team_landing" },
];

export function setCurrentPageKey(pathname: string): void {
  const hit = ROUTE_TO_PAGE.find((r) => r.match(pathname));
  if (hit) {
    currentPageKey = hit.page;
  } else {
    currentPageKey = "unknown";
    if (typeof console !== "undefined") {
      console.warn(`[analytics] unmapped route "${pathname}" -> page_name "unknown"`);
    }
  }
}

export function getCurrentPageKey(): string {
  return currentPageKey;
}
